import { every } from 'lodash'
import { debounce } from 'throttle-debounce'
import * as React from 'react'
import { connect } from 'react-redux'

import { AprType, LOAN_PURPOSE_LIST, LoanOfferProductSubType, LoanRatesFilter } from 'embed/constants'

import * as actions from 'common/actions'
import { ConfigKeys, Filter, Filters, GoogleAnalyticsActions, LoanPurposeOptions, Product } from 'even-ts-static'
import View from './index'

import { validationDataSelector } from 'embed/selectors'
import { configStateSelector, isDotDashSelector } from 'embed/selectors/config'
import { ValidationData } from 'common/services/Validation'
import { Fields } from 'embed/constants/fields'
import { MultiProductState } from 'embed/reducers'
import { filtersLoanSelector } from 'embed/selectors/filters'
import { uiViewportHeightSelector } from 'embed/selectors/ui'

export interface LoanFilterStyles {
  wrapper: string
}

export interface Props {
  creditRatingLabel?: string
  dispatchFetchLoanRateTables: () => void
  dispatchFetchZipcodeFromIP: () => void
  dispatchFilterChanged: (filter: Filter, value: unknown, userInitiated?: boolean) => void
  dispatchFiltersChanged: (filters: Filters) => void
  dispatchRatesFilterChanged: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) => void
  dispatchTrackGoogleAnalyticsFilterChanged: (filter: Filter) => void
  filters: Filters
  isDotDash: boolean
  resultsUuid?: string
  styles?: LoanFilterStyles
  validationData: ValidationData
}

const Container: React.FC<Props> = ({
  dispatchFetchLoanRateTables,
  dispatchFetchZipcodeFromIP,
  dispatchFilterChanged,
  dispatchRatesFilterChanged,
  dispatchTrackGoogleAnalyticsFilterChanged,
  filters,
  resultsUuid,
  validationData,
  ...props
}) => {
  React.useEffect(() => {
    // Fetch Zipcode from IP if we haven't set it yet from a query param
    if (filters.zipcode === undefined) {
      dispatchFilterChanged(Filter.Zipcode, '10010') // fallback zip if geo-ip fails
      dispatchFetchZipcodeFromIP()
    }

    // Load initial results (if a result uuid was not specified in the config)
    if (resultsUuid === undefined) {
      dispatchFetchLoanRateTables()
    }
  }, [])

  const onChange = React.useMemo(() => {
    const debouncedFetch = debounce(500, true, (filter: Filter, value) => {
      const allFiltersValid = every(filters, (filterValue, key) => {
        const field = Object.values(Fields).find((el) => el.key === key) || undefined
        if (field) {
          return key === filter
            ? field.validate(value, validationData)
            : field.validate(filterValue || '', validationData)
        } else {
          return false
        }
      })
      if (allFiltersValid) {
        dispatchFetchLoanRateTables()
      }
    })
    return (filter: Filter, value) => {
      dispatchTrackGoogleAnalyticsFilterChanged(filter)
      dispatchFilterChanged(filter, value, true)
      debouncedFetch(filter, value)
    }
  }, [dispatchTrackGoogleAnalyticsFilterChanged, dispatchFetchLoanRateTables, dispatchFilterChanged])

  const loanPurposeOnChange = React.useCallback(
    (filter: Filter, value) => {
      if (value === LoanPurposeOptions.STUDENT_REFINANCE) {
        // switching to SLR
        dispatchRatesFilterChanged(LoanRatesFilter.ProductSubType, LoanOfferProductSubType.StudentLoanRefinance, true)
      } else {
        // switching away from SLR, reset ratesFilters
        dispatchRatesFilterChanged(LoanRatesFilter.ProductSubType, LoanOfferProductSubType.PersonalLoan, true)
        dispatchRatesFilterChanged(LoanRatesFilter.AprType, AprType.Fixed, true)
      }
      onChange(filter, value)
    },
    [dispatchRatesFilterChanged, onChange],
  )

  return <View onChange={onChange} loanPurposeOnChange={loanPurposeOnChange} filters={filters} {...props} />
}

const mapStateToProps = (state: MultiProductState) => {
  const loanPurposeConfigValues = configStateSelector(state)[ConfigKeys.loanPurposeOptions] ?? {}
  return {
    filters: filtersLoanSelector(state) ?? {},
    isDotDash: isDotDashSelector(state),
    loanPurposeItems: LOAN_PURPOSE_LIST.filter((option) =>
      Object.values(loanPurposeConfigValues).includes(option.value as LoanPurposeOptions),
    ),
    resultsUuid: state.config[ConfigKeys.ResultsUuid],
    validationData: validationDataSelector(state),
    viewportHeight: uiViewportHeightSelector(state),
  }
}

const mapDispatchToProps = (dispatch) => ({
  dispatchFetchLoanRateTables: () => dispatch(actions.fetchPreviewLoanOffers()),
  dispatchFetchZipcodeFromIP: () => dispatch(actions.fetchZipcodeFromIP(Product.Loan, false)),
  dispatchFilterChanged: (filter: Filter, value: unknown, userInitiated?: boolean) =>
    dispatch(actions.filterChanged(Product.Loan, filter, value, { userInitiated })),
  dispatchFiltersChanged: (filters) => dispatch(actions.filtersChanged(Product.Loan, filters)),
  dispatchRatesFilterChanged: (filterType: LoanRatesFilter, filterValue: unknown, replace: boolean) =>
    dispatch(actions.ratesFilterChanged(Product.Loan, filterType, filterValue, replace)),
  dispatchTrackGoogleAnalyticsFilterChanged: (filter: Filter) =>
    dispatch(actions.trackGoogleAnalyticsEvent(GoogleAnalyticsActions.Filtered, undefined, undefined, filter)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Container)
